import axios from 'axios';
import moment from 'moment';
import { cache, bottleneck, formatNumber, addressToString, CountryCode, } from '../index.js';
import helpers from '../economy/helpers/index.js';
import { VoucherCategory } from '../economy/vouchers/index.js';
import { TemplateTag } from '../templates/index.js';
import { UpdateType } from '../updates/index.js';
import { sortBy } from '../util/index.js';
import { DKcourts } from './countries/denmark.js';
import { GLcourts } from './countries/greenland.js';
export const getRetskredsDenmark = async (addr) => {
    try {
        const res = (await cache(1000 * 60 * 60, (a) => bottleneck('DAWAAPI', 333, () => axios.get('https://dawa.aws.dk/datavask/adgangsadresser?betegnelse=' +
            encodeURIComponent(a))), 'https://dawa.aws.dk/datavask/adgangsadresser?betegnelse=' + addr, false, undefined, addr)).data;
        if (!res.resultater[0].adresse.href &&
            !res.resultater[0].aktueladresse.href)
            throw new Error('Could not datawash address for finding court');
        const address = (await cache(1000 * 60 * 60, (href) => bottleneck('DAWAAPI', 333, () => axios.get(href)), res.resultater[0].adresse.href || res.resultater[0].aktueladresse.href, false, undefined, res.resultater[0].adresse.href || res.resultater[0].aktueladresse.href)).data;
        const court = (await cache(1000 * 60 * 60 * 24, (kode) => bottleneck('DAWAAPI', 333, () => axios.get('https://dawa.aws.dk/retskredse/' + kode)), 'https://dawa.aws.dk/retskredse/' + address.retskreds.kode, false, undefined, address.retskreds.kode)).data;
        const c = courts.find((c) => c.id === court.kode);
        return {
            ...c,
            ...court,
            address: c,
        };
    }
    catch (e) {
        if (e.response) {
            console.error(e.response.data);
            console.error(e.response.status);
            console.error(e.response.headers);
        }
        else if (e.request) {
            console.error(e.request);
        }
        else if (e.message) {
            console.error('Error', e.message);
        }
        else {
            console.error(e);
        }
    }
};
export var UdbetalingsRekvisitioner;
(function (UdbetalingsRekvisitioner) {
    UdbetalingsRekvisitioner["Politifremstilling"] = "Politifremstilling";
    UdbetalingsRekvisitioner["Betalingsp\u00E5krav"] = "Betalingsp\u00E5krav";
    UdbetalingsRekvisitioner["Fogedrekvisition"] = "Fogedrekvisition";
})(UdbetalingsRekvisitioner || (UdbetalingsRekvisitioner = {}));
export const courts = [...DKcourts, ...GLcourts];
export const courtByCountryCode = {
    [CountryCode.DK]: DKcourts,
    [CountryCode.GL]: [...GLcourts, ...DKcourts],
};
export const getCourtByCountryCode = (countryCode) => {
    if (countryCode && courtByCountryCode[countryCode]) {
        return courtByCountryCode[countryCode];
    }
    return null;
};
export const getDirector = (debtors) => {
    const virksomhedsformer = [
        'Anpartsselskab',
        'Aktieselskab',
        'Iværksætterselskab',
    ];
    const relationReasons = ['DIREKTØR', 'ADM. DIR.', 'DIREKTION'];
    const ff = debtors.reduce((acc, debtor) => {
        if (debtor.details.virksomhedsform &&
            virksomhedsformer.includes(debtor.details.virksomhedsform)) {
            return acc.concat((debtor.details.companyRelations || []).reduce((abb, relation) => {
                if (relation.type === 'PERSON' &&
                    relation.name &&
                    relation.relationReasons.some((rr) => relationReasons.includes(rr.value))) {
                    return abb.concat({
                        name: `${relation.name} (${relation.relationReasons
                            .filter((rr) => relationReasons.includes(rr.value))
                            .map((rr) => rr.value)
                            .join(', ')})`,
                        address: relation.address?.address &&
                            relation.address?.address !== 'null'
                            ? relation.address
                            : null,
                    });
                }
                return abb;
            }, []));
        }
        return acc;
    }, []);
    return ff && !!ff.length ? ff[0] : null;
};
export const getFuldtAnsvaligeDeltagere = (debtors) => {
    const virksomhedsformer = [
        'Enkeltmandsvirksomhed',
        'Interessentskab',
        'Personligt ejet Mindre Virksomhed',
    ];
    const ff = debtors.reduce((acc, debtor) => {
        if (debtor.details.virksomhedsform &&
            virksomhedsformer.includes(debtor.details.virksomhedsform)) {
            return acc.concat((debtor.details.companyRelations || []).reduce((abb, relation) => {
                if (relation.type === 'PERSON' && relation.name) {
                    return abb.concat({
                        name: relation.name,
                        address: relation.address,
                    });
                }
                return abb;
            }, []));
        }
        return acc;
    }, []);
    return ff && !!ff.length ? ff : null;
};
export const getInterestDates = (caseVouchers, interestDates) => interestDates ||
    (caseVouchers &&
        helpers.vouchers
            .postProcessing(caseVouchers, {
            includeIrrecoverableVouchers: false,
            includeDeletedVouchers: false,
            includeExpiredVouchers: false,
        })
            .filter((c) => c.category === 'interest')
            .filter((c) => c.interestStartDate || c.dueDate)
            .filter((c) => c.interestStartDate || c.dueDate)
            .map((c) => c.interestStartDate || c.dueDate)
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
            .map((d) => moment(d).locale('da').format('LL'))
            .join(', ')) ||
    '';
export const getInterests = (caseVouchers, interests) => interests ||
    (caseVouchers &&
        formatNumber(-helpers.overview.balanceCategory(caseVouchers, VoucherCategory.INTEREST))) ||
    '';
export const getTotalAmount = (caseSeqId, caseVouchers, totalAmount) => {
    if (totalAmount)
        return totalAmount;
    if (!caseSeqId)
        return '0';
    const balance = caseVouchers && -helpers.overview.balance(caseVouchers);
    return (!!balance && formatNumber(balance)) || '';
};
export const getCaseInterest = (caseVouchers, customInterest) => {
    if (customInterest)
        return customInterest;
    const interestOnCase = caseVouchers.find((v) => v.category === VoucherCategory.INTEREST)
        ?.interest || '';
    const caseInterest = typeof interestOnCase == 'number'
        ? (interestOnCase * 100).toFixed(2).toString().replace('.', ',')
        : interestOnCase;
    return caseInterest;
};
export const getWarnings = (interactions, templates) => {
    const warningTemplates = templates.reduce((acc, template) => {
        if (template.tags.map(({ tag }) => tag).includes(TemplateTag.BPKWarning)) {
            acc[template.templateId] = true;
        }
        return acc;
    }, {});
    const warnings = interactions.filter((interaction) => {
        const isWarning = interaction.templateId && warningTemplates[interaction.templateId];
        if (isWarning) {
            return interaction;
        }
    });
    return sortBy(warnings, (w) => w.createdAt);
};
export const getWarningDate = (interactions, updates, templates) => {
    const warning = getWarnings(interactions, templates)[0];
    const warningUpdate = getWarningUpdates(updates)[0];
    const warningDate = warning ? warning.createdAt : null;
    if (warningDate && warningUpdate) {
        if (moment(warningDate).isBefore(moment(warningUpdate.pausedAt))) {
            return moment(warningDate).format();
        }
        return moment(warningUpdate.pausedAt).format();
    }
    else if (warningUpdate) {
        return moment(warningUpdate.pausedAt).format();
    }
    else {
        return warningDate;
    }
};
export const getWarningUpdates = (updates) => {
    const warningUpdates = updates.filter((update) => {
        if (update.updateTypeId === UpdateType.BPKWARNED) {
            return true;
        }
        return false;
    });
    return sortBy(warningUpdates, (w) => w.pausedAt);
};
export const getCourt = (contactPerson, court) => {
    if (court || court === null)
        return court;
    const contactPersonCourt = contactPerson &&
        contactPerson.details.courtId &&
        courts.find((c) => contactPerson.details.courtId &&
            [c.id, c.courtId].includes(contactPerson.details.courtId));
    if (contactPersonCourt) {
        return contactPersonCourt;
    }
};
export const getCourtFromAddress = async (addr) => {
    const address = addressToString(addr, { noCoAddress: true, noCountry: true });
    if (!address) {
        throw new Error('Could not find address');
    }
    const court = await getRetskredsDenmark(address);
    return court || null;
};
//# sourceMappingURL=index.js.map